import { Box, Autocomplete, TextField } from '@mui/material';
import React, { useCallback, useMemo, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import type Stripe from 'stripe';
import { z } from 'zod';

export const StripeCustomersDropdownFormSchema = z.object({
  customerId: z.string().nonempty(),
  startDate: z.date(),
});
export type StripeCustomersDropdownForm = z.infer<typeof StripeCustomersDropdownFormSchema>;

type StripeCustomersDropdownProps = {
  menuItemClickHandler?: (customerId: string) => void;
  customers: Stripe.Customer[];
};

export const StripeCustomersDropdown: React.FC<StripeCustomersDropdownProps> = ({ menuItemClickHandler, customers }) => {
  const [query, setQuery] = useState('');
  const { getFieldState, resetField, setValue } = useFormContext<StripeCustomersDropdownForm>();
  const hasError = getFieldState('customerId')?.invalid;

  const filteredCustomers = useMemo(() => {
    return Object.values(customers).filter((customer) => {
      return (customer.name ?? customer.email ?? customer.id).toLowerCase().includes(query.toLowerCase());
    });
  }, [query, customers]);

  const isAvailableCustomer = useCallback((customerName: string) => {
    return Object.values(customers).some(({ name, email, id }) => (name ?? email ?? id).toLowerCase() === customerName.toLowerCase());
  }, [customers]);

  const handleCustomerSelect = useCallback((countryName: string) => {
    const customer = Object.values(customers).find(({ name, email, id }) => (name ?? email ?? id).toLowerCase() === countryName.toLowerCase())?.id ?? '';

    setQuery(countryName);
    menuItemClickHandler?.(customer);

    isAvailableCustomer(countryName)
      ? setValue('customerId', customer)
      : resetField('customerId');
  }, [menuItemClickHandler]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
      <Autocomplete
        value={query}
        onChange={(_, value) => handleCustomerSelect(value ?? '')}
        options={filteredCustomers.map((customer) => customer.name ?? '')}
        renderInput={(params) => (
          <TextField
            {...params}            
            fullWidth
            label="Select your Stripe customer"
            variant="filled"
            size='medium'
            error={hasError}
            helperText={hasError && getFieldState('customerId')?.error?.message}
          />
        )}
      />
    </Box>
  );
};